/**
 * HW Victoria reference — the timing core for the tidal-stream diamonds.
 *
 * The diamonds and the stream atlas are all referred to HW Victoria. Victoria is
 * a Standard Port (Time Zone UT). This module now takes its HW Victoria timing
 * AND its spring/neap state from the shared tidal-HEIGHT model (`./heights`, the
 * Victoria HeightPort built from the RYA eNav HW/LW feed), so the streams and the
 * heights are driven by ONE source instead of a separate HW-only table. Both the
 * stream overlay and the Conditions height card now move together off the same
 * Victoria data.
 *
 * The training data carries no calendar year, so the height model anchors to a
 * notional year whose 1 Jan is a Tuesday (2019), matching the almanac's "1 TU".
 * Nothing depends on the real year.
 */
import { getHeightPort, springRatioFromRange, type HeightEvent } from './heights';

const VICTORIA_YEAR = 2019;

export interface HwEvent {
  /** UTC epoch milliseconds. */
  t: number;
  /** HW height, metres above chart datum. */
  heightM: number;
}

/** The shared Victoria height port — the single source for timing + spring/neap. */
const victoriaPort = getHeightPort('victoria');

/** HW Victoria for the full training year, from the shared height model. */
export const VICTORIA_HW: HwEvent[] = victoriaPort.events
  .filter((e) => e.type === 'H')
  .map((e) => ({ t: e.t, heightM: e.heightM }));

/**
 * Victoria tidal levels (chart "Tidal Levels referred to Datum of Soundings"),
 * kept for reference/display. Spring/neap is now taken from the height model's
 * mean ranges via springRatioFromRange, not these, so the streams and the height
 * card agree on what counts as springs.
 */
export const VICTORIA_MHWS = 5.6;
export const VICTORIA_MHWN = 4.4;

const SIX_H = 6 * 3_600_000;

/** Scrubbable window: 6 h either side of the first/last HW (the whole year). */
export const VICTORIA_WINDOW = {
  startMs: VICTORIA_HW[0].t - SIX_H,
  endMs: VICTORIA_HW[VICTORIA_HW.length - 1].t + SIX_H,
};

/** A sensible default training moment inside the window (a springs afternoon). */
export const VICTORIA_DEFAULT_MS = Date.UTC(VICTORIA_YEAR, 0, 4, 12, 0);

/** The HW Victoria closest in time to `ms`. */
export function nearestHW(ms: number): HwEvent {
  let best = VICTORIA_HW[0];
  for (const hw of VICTORIA_HW) {
    if (Math.abs(hw.t - ms) < Math.abs(best.t - ms)) best = hw;
  }
  return best;
}

/**
 * Spring/neap state from a HW height (legacy helper): 0 at MHWN, 1 at MHWS.
 * Retained for reference; tidalStateAt now uses the height model's range-based
 * spring/neap so streams and heights agree.
 */
export function springRatioFromHeight(heightM: number): number {
  return Math.max(0, Math.min(1, (heightM - VICTORIA_MHWN) / (VICTORIA_MHWS - VICTORIA_MHWN)));
}

export interface TidalState {
  /** Hours relative to the nearest HW Victoria (negative = before HW). */
  hoursFromHW: number;
  /** Spring/neap state for that HW, 0 = neaps, 1 = springs. */
  springRatio: number;
  /** The nearest HW used. */
  hw: HwEvent;
}

/** The fall from a HW to its adjacent LW (the cycle range), from the height events. */
function rangeAtHwIndex(events: HeightEvent[], i: number): number {
  const hw = events[i];
  let range = 0;
  if (events[i - 1]) range = Math.max(range, Math.abs(hw.heightM - events[i - 1].heightM));
  if (events[i + 1]) range = Math.max(range, Math.abs(hw.heightM - events[i + 1].heightM));
  return range;
}

/**
 * Hours-from-HW and spring/neap state for an instant, both from the shared
 * Victoria height model: the nearest High Water, and the spring/neap read off
 * that cycle's range against Victoria's mean spring and neap ranges.
 */
export function tidalStateAt(ms: number): TidalState {
  const ev = victoriaPort.events;
  let bi = -1;
  let bd = Infinity;
  for (let i = 0; i < ev.length; i++) {
    if (ev[i].type !== 'H') continue;
    const d = Math.abs(ev[i].t - ms);
    if (d < bd) {
      bd = d;
      bi = i;
    }
  }
  const hw = ev[bi];
  const springRatio = springRatioFromRange(victoriaPort, rangeAtHwIndex(ev, bi));
  return { hoursFromHW: (ms - hw.t) / 3_600_000, springRatio, hw: { t: hw.t, heightM: hw.heightM } };
}
